import { useEffect, useState } from "react";
import * as React from "react";

import {
  Button,
  FormHelperText,
  Grid,
  InputLabel,
  Stack,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  Divider,
  DialogActions,
  Box,
  Autocomplete,
  Typography,
  useTheme,
  CircularProgress,
} from "@mui/material";

import * as Yup from "yup";

import { openSnackbar } from "../../../../../api/snackbar";
import { USERAXIOS } from "../../../../../api";
import { SnackbarProps } from "../../../../../types/snackbar";
import { FormattedMessage, useIntl } from "react-intl";
import {
  DegreeType,
  EducationalInstitution,
  ResumeEducation,
} from "../../../../../types/resumes-managment/resume";
import { useFormik, FormikProvider, Form } from "formik";

import { Country, City } from "../../../../../types/site-params/Country";
import ImageUploader from "../../../../../components/ImageUploader";
import { useCountriesForAutocomplete } from "../../../../../hooks/query-hooks/useCountries";
import { useCitiesForAutocomplete } from "../../../../../hooks/query-hooks/useCities";
import { useDegreeTypesForAutocomplete } from "../../../../../hooks/query-hooks/useDegreeTypes";
import { useEducationalInstitutionsForAutocomplete } from "../../../../../hooks/query-hooks/useEducationalInstitutions";
import { useDebounce } from "../../../../../hooks";

interface ServerErrors {
  [key: string]: string[];
}

const getInitialValues = (education: ResumeEducation | null) => {
  if (education && education.id) {
    return {
      educatioanl_institution_id: education.educatioanl_institution_id || null,
      degree_type_id: education.degree_type_id || null,
      certificate_name: education.certificate_name || "",
      description: education.description || "",
      specialization: education.specialization || "",
      average: education.average || 0,
      graduation_year: education.graduation_year || new Date().getFullYear(),
      country_id: education.country_id || null,
      city_id: education.city_id || null,
      certificate_photo: education.certificate_photo || null,
    };
  } else {
    return {
      educatioanl_institution_id: null,
      degree_type_id: null,
      certificate_name: "",
      description: "",
      specialization: "",
      average: 0,
      graduation_year: new Date().getFullYear(),
      country_id: null,
      city_id: null,
      certificate_photo: null,
    };
  }
};

const EducationSchema = Yup.object().shape({
  educatioanl_institution_id: Yup.number().required(
    "Educational Institution is required"
  ),
  degree_type_id: Yup.number().required("Degree Type is required"),
  certificate_name: Yup.string().required("Certificate Name is required"),
  description: Yup.string().required("Description is required"),
  specialization: Yup.string().required("Specialization is required"),
  average: Yup.number().required("Average is required").min(0).max(100),
  graduation_year: Yup.number()
    .required("Graduation Year is required")
    .min(1900)
    .max(new Date().getFullYear() + 10),
  country_id: Yup.number().required("Country is required"),
  city_id: Yup.number().nullable(),
  certificate_photo: Yup.string().nullable(),
});

function AddEditResumeEducationForm({
  setResult,
  resumeId,
  open,
  setOpen,
  item,
}: {
  setResult: any;
  resumeId: any;
  open: boolean;
  setOpen: (openLanguage: boolean) => void;
  item: ResumeEducation | null;
}) {
  const [serverErrors, setServerErrors] = useState<ServerErrors>({});

  // Search states for autocomplete
  const [countrySearchTerm, setCountrySearchTerm] = useState("");
  const [citySearchTerm, setCitySearchTerm] = useState("");
  const [degreeTypeSearchTerm, setDegreeTypeSearchTerm] = useState("");
  const [
    educationalInstitutionSearchTerm,
    setEducationalInstitutionSearchTerm,
  ] = useState("");

  // Debounced search terms
  const debouncedCountrySearchTerm = useDebounce(countrySearchTerm, 500);
  const debouncedCitySearchTerm = useDebounce(citySearchTerm, 500);
  const debouncedDegreeTypeSearchTerm = useDebounce(degreeTypeSearchTerm, 500);
  const debouncedEducationalInstitutionSearchTerm = useDebounce(
    educationalInstitutionSearchTerm,
    500
  );

  const theme = useTheme();

  const handleClose = () => {
    setOpen(false);
    setServerErrors({});
  };

  const getServerError = (fieldName: string): string | null => {
    if (serverErrors[fieldName] && serverErrors[fieldName].length > 0) {
      return serverErrors[fieldName][0];
    }
    return null;
  };

  const formik = useFormik({
    initialValues: getInitialValues(item ?? null),
    validationSchema: EducationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      setServerErrors({});

      try {
        const formData = new FormData();
        formData.append("resume_id", resumeId);
        formData.append(
          "educatioanl_institution_id",
          values.educatioanl_institution_id?.toString() ?? ""
        );
        formData.append(
          "degree_type_id",
          values.degree_type_id?.toString() ?? ""
        );
        formData.append("certificate_name", values.certificate_name);
        formData.append("description", values.description);
        formData.append("specialization", values.specialization);
        formData.append("average", values.average.toString());
        formData.append("graduation_year", values.graduation_year.toString());
        formData.append("country_id", values.country_id?.toString() ?? "");
        formData.append("city_id", values.city_id?.toString() ?? "");
        if (
          values?.certificate_photo &&
          typeof values.certificate_photo !== "string"
        ) {
          formData.append("certificate_photo", values.certificate_photo);
        }

        if (item && item?.id) {
          const response = await USERAXIOS.post(
            `resume/educations/${item?.id}`,
            formData,
            {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            }
          );
          const updatedExperience = response.data.data;

          if (updatedExperience) {
            setResult((prev: ResumeEducation[]) =>
              prev.map((item) =>
                item.id === updatedExperience.id ? updatedExperience : item
              )
            );

            openSnackbar({
              open: true,
              message: "Resume education updated successfully",
              anchorOrigin: { vertical: "top", horizontal: "right" },
              variant: "alert",
              alert: { color: "success" },
            } as SnackbarProps);
          }
        } else {
          const response = await USERAXIOS.post("resume/educations", formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          });
          const newExperience = response.data.data;

          if (newExperience) {
            setResult((prev: ResumeEducation[]) => [...prev, newExperience]);

            openSnackbar({
              open: true,
              message: "Resume education Added successfully",
              anchorOrigin: { vertical: "top", horizontal: "right" },
              variant: "alert",
              alert: { color: "success" },
            } as SnackbarProps);
          }
        }

        setSubmitting(false);
        handleClose();
      } catch (error: any) {
        if (error.response?.data?.message) {
          setServerErrors(error.response.data.message);
          console.log("Error ", error.response.data.message);
        } else {
          console.error("Error ", error);
        }

        openSnackbar({
          open: true,
          message: "Something went wrong",
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "alert",
          alert: { color: "error" },
        } as SnackbarProps);
        setSubmitting(false);
      }
    },
  });

  const {
    errors,
    touched,
    handleSubmit,
    isSubmitting,
    setFieldValue,
    values,
    handleChange,
  } = formik;

  const {
    items: countries,
    loading: loadingCountries,
    hasMore: hasMoreCountries,
    loadMore: loadMoreCountries,
    error: countriesError,
  } = useCountriesForAutocomplete(20, debouncedCountrySearchTerm);

  const {
    items: cities,
    loading: loadingCities,
    hasMore: hasMoreCities,
    loadMore: loadMoreCities,
    error: citiesError,
  } = useCitiesForAutocomplete(values?.country_id, 20, debouncedCitySearchTerm);

  const {
    items: degreeTypes,
    loading: loadingDegreeTypes,
    hasMore: hasMoreDegreeTypes,
    loadMore: loadMoreDegreeTypes,
    error: degreeTypesError,
  } = useDegreeTypesForAutocomplete(20, debouncedDegreeTypeSearchTerm);

  const {
    items: educationalInstitutions,
    loading: loadingEducationalInstitutions,
    hasMore: hasMoreEducationalInstitutions,
    loadMore: loadMoreEducationalInstitutions,
    error: educationalInstitutionsError,
  } = useEducationalInstitutionsForAutocomplete(
    20,
    debouncedEducationalInstitutionSearchTerm
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle
        color="primary"
        style={{
          textAlign: "center",
          fontSize: "22px",
          fontWeight: "bold",
          marginBottom: "10px",
        }}
      >
        {item && item?.id ? (
          <FormattedMessage id="Edit Education" />
        ) : (
          <FormattedMessage id="Add Education" />
        )}
      </DialogTitle>
      <Divider />

      <DialogContent
        sx={{ height: "100%", display: "flex", flexDirection: "column", p: 2 }}
      >
        <FormikProvider value={formik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <Grid container spacing={3} sx={{ flex: 1, overflowY: "auto" }}>
              <Grid item xs={12} sm={6}>
                <Stack spacing={1}>
                  <Typography>
                    <FormattedMessage id="Educational Institution" />
                  </Typography>
                  <Autocomplete
                    id="educational-institution-select"
                    options={educationalInstitutions}
                    getOptionLabel={(option: EducationalInstitution) =>
                      theme.direction === "rtl"
                        ? option.name_trans?.ar ?? ""
                        : option.name ?? ""
                    }
                    value={
                      educationalInstitutions.find(
                        (institution) =>
                          institution.id === values.educatioanl_institution_id
                      ) || null
                    }
                    onChange={(_, newValue) => {
                      setFieldValue(
                        "educatioanl_institution_id",
                        newValue?.id || null
                      );
                      // Clear search term when option is selected
                      if (newValue) {
                        setEducationalInstitutionSearchTerm("");
                      }
                    }}
                    onInputChange={(event, newInputValue, reason) => {
                      if (reason === "input") {
                        setEducationalInstitutionSearchTerm(newInputValue);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select educational institution"
                        error={
                          (touched.educatioanl_institution_id &&
                            Boolean(errors.educatioanl_institution_id)) ||
                          Boolean(getServerError("educatioanl_institution_id"))
                        }
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingEducationalInstitutions && (
                                <CircularProgress color="inherit" size={20} />
                              )}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    ListboxProps={{
                      onScroll: (event) => {
                        const listboxNode = event.currentTarget;
                        const threshold = 100;
                        if (
                          listboxNode.scrollHeight - listboxNode.scrollTop <=
                            listboxNode.clientHeight + threshold &&
                          hasMoreEducationalInstitutions &&
                          !loadingEducationalInstitutions
                        ) {
                          loadMoreEducationalInstitutions();
                        }
                      },
                    }}
                    loading={loadingEducationalInstitutions}
                    filterOptions={(x) => x} // Disable built-in filtering
                    noOptionsText={
                      debouncedEducationalInstitutionSearchTerm.length >= 2
                        ? "No educational institutions found"
                        : "Type to search educational institutions"
                    }
                  />
                  {touched.educatioanl_institution_id &&
                    errors.educatioanl_institution_id && (
                      <FormHelperText error>
                        {errors.educatioanl_institution_id}
                      </FormHelperText>
                    )}
                  {getServerError("educatioanl_institution_id") && (
                    <FormHelperText error>
                      {getServerError("educatioanl_institution_id")}
                    </FormHelperText>
                  )}
                </Stack>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Stack spacing={1}>
                  <Typography>
                    <FormattedMessage id="Degree Type" />
                  </Typography>
                  <Autocomplete
                    id="degree-type-select"
                    options={degreeTypes}
                    getOptionLabel={(option: DegreeType) =>
                      theme.direction === "rtl"
                        ? option.name_trans?.ar ?? option.name
                        : option.name
                    }
                    value={
                      degreeTypes.find(
                        (degreeType) => degreeType.id === values.degree_type_id
                      ) || null
                    }
                    onChange={(_, newValue) => {
                      setFieldValue("degree_type_id", newValue?.id || null);
                      // Clear search term when option is selected
                      if (newValue) {
                        setDegreeTypeSearchTerm("");
                      }
                    }}
                    onInputChange={(event, newInputValue, reason) => {
                      if (reason === "input") {
                        setDegreeTypeSearchTerm(newInputValue);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select degree type"
                        error={
                          (touched.degree_type_id &&
                            Boolean(errors.degree_type_id)) ||
                          Boolean(getServerError("degree_type_id"))
                        }
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingDegreeTypes && (
                                <CircularProgress color="inherit" size={20} />
                              )}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    ListboxProps={{
                      onScroll: (event) => {
                        const listboxNode = event.currentTarget;
                        const threshold = 100;
                        if (
                          listboxNode.scrollHeight - listboxNode.scrollTop <=
                            listboxNode.clientHeight + threshold &&
                          hasMoreDegreeTypes &&
                          !loadingDegreeTypes
                        ) {
                          loadMoreDegreeTypes();
                        }
                      },
                    }}
                    loading={loadingDegreeTypes}
                    filterOptions={(x) => x} // Disable built-in filtering
                    noOptionsText={
                      debouncedDegreeTypeSearchTerm.length >= 2
                        ? "No degree types found"
                        : "Type to search degree types"
                    }
                  />
                  {touched.degree_type_id && errors.degree_type_id && (
                    <FormHelperText error>
                      {errors.degree_type_id}
                    </FormHelperText>
                  )}
                  {getServerError("degree_type_id") && (
                    <FormHelperText error>
                      {getServerError("degree_type_id")}
                    </FormHelperText>
                  )}
                </Stack>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="certificate-name">
                    <FormattedMessage id="Certificate Name" />
                  </InputLabel>
                  <TextField
                    fullWidth
                    id="certificate-name"
                    name="certificate_name"
                    value={values.certificate_name}
                    onChange={handleChange}
                    placeholder="Certificate Name"
                    error={
                      (touched.certificate_name &&
                        Boolean(errors.certificate_name)) ||
                      Boolean(getServerError("certificate_name"))
                    }
                  />
                  {touched.certificate_name && errors.certificate_name && (
                    <FormHelperText error>
                      {errors.certificate_name}
                    </FormHelperText>
                  )}
                  {getServerError("certificate_name") && (
                    <FormHelperText error>
                      {getServerError("certificate_name")}
                    </FormHelperText>
                  )}
                </Stack>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="specialization">
                    <FormattedMessage id="Specialization" />
                  </InputLabel>
                  <TextField
                    fullWidth
                    id="specialization"
                    name="specialization"
                    value={values.specialization}
                    onChange={handleChange}
                    placeholder="Specialization"
                    error={
                      (touched.specialization &&
                        Boolean(errors.specialization)) ||
                      Boolean(getServerError("specialization"))
                    }
                  />
                  {touched.specialization && errors.specialization && (
                    <FormHelperText error>
                      {errors.specialization}
                    </FormHelperText>
                  )}
                  {getServerError("specialization") && (
                    <FormHelperText error>
                      {getServerError("specialization")}
                    </FormHelperText>
                  )}
                </Stack>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="average">
                    <FormattedMessage id="Average" />
                  </InputLabel>
                  <TextField
                    fullWidth
                    id="average"
                    name="average"
                    type="number"
                    value={values.average}
                    onChange={handleChange}
                    placeholder="Average (0-100)"
                    inputProps={{ min: 0, max: 100, step: 0.01 }}
                    error={
                      (touched.average && Boolean(errors.average)) ||
                      Boolean(getServerError("average"))
                    }
                  />
                  {touched.average && errors.average && (
                    <FormHelperText error>{errors.average}</FormHelperText>
                  )}
                  {getServerError("average") && (
                    <FormHelperText error>
                      {getServerError("average")}
                    </FormHelperText>
                  )}
                </Stack>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="graduation-year">
                    <FormattedMessage id="Graduation Year" />
                  </InputLabel>
                  <TextField
                    fullWidth
                    id="graduation-year"
                    name="graduation_year"
                    type="number"
                    value={values.graduation_year}
                    onChange={(e) => {
                      const value = e.target.value
                        .replace(/[^0-9]/g, "")
                        .slice(0, 4);
                      setFieldValue("graduation_year", value);
                    }}
                    placeholder="Year (e.g. 2024)"
                    inputProps={{ maxLength: 4 }}
                    error={
                      (touched.graduation_year &&
                        Boolean(errors.graduation_year)) ||
                      Boolean(getServerError("graduation_year"))
                    }
                  />
                  {touched.graduation_year && errors.graduation_year && (
                    <FormHelperText error>
                      {errors.graduation_year}
                    </FormHelperText>
                  )}
                  {getServerError("graduation_year") && (
                    <FormHelperText error>
                      {getServerError("graduation_year")}
                    </FormHelperText>
                  )}
                </Stack>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Stack spacing={1}>
                  <Typography>
                    <FormattedMessage id="Country" />
                  </Typography>
                  <Autocomplete
                    id="country-select"
                    options={countries}
                    getOptionLabel={(option: Country) =>
                      theme.direction === "rtl"
                        ? option.name_trans.ar
                        : option.name
                    }
                    value={
                      countries?.find(
                        (country) => country.id === values.country_id
                      ) || null
                    }
                    onChange={(_, newValue) => {
                      const selectedCountryId = newValue?.id || null;
                      setFieldValue("country_id", selectedCountryId);
                      setFieldValue("city_id", null);
                      // Clear search term when option is selected
                      if (newValue) {
                        setCountrySearchTerm("");
                      }
                    }}
                    onInputChange={(event, newInputValue, reason) => {
                      if (reason === "input") {
                        setCountrySearchTerm(newInputValue);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select country"
                        error={
                          (touched.country_id && Boolean(errors.country_id)) ||
                          Boolean(getServerError("country_id"))
                        }
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingCountries && (
                                <CircularProgress color="inherit" size={20} />
                              )}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    ListboxProps={{
                      onScroll: (event) => {
                        const listboxNode = event.currentTarget;
                        const threshold = 100;
                        if (
                          listboxNode.scrollHeight - listboxNode.scrollTop <=
                            listboxNode.clientHeight + threshold &&
                          hasMoreCountries &&
                          !loadingCountries
                        ) {
                          loadMoreCountries();
                        }
                      },
                    }}
                    loading={loadingCountries}
                    filterOptions={(x) => x} // Disable built-in filtering
                    noOptionsText={
                      debouncedCountrySearchTerm.length >= 2
                        ? "No countries found"
                        : "Type to search countries"
                    }
                  />
                  {touched.country_id && errors.country_id && (
                    <FormHelperText error>{errors.country_id}</FormHelperText>
                  )}
                  {getServerError("country_id") && (
                    <FormHelperText error>
                      {getServerError("country_id")}
                    </FormHelperText>
                  )}
                </Stack>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Stack spacing={1}>
                  <Typography>
                    <FormattedMessage id="City" />
                  </Typography>
                  <Autocomplete
                    id="city-select"
                    options={cities}
                    getOptionLabel={(option: City) =>
                      theme.direction === "rtl"
                        ? option.name_trans.ar
                        : option.name
                    }
                    value={
                      cities.find((city) => city.id === values.city_id) || null
                    }
                    onChange={(_, newValue) => {
                      setFieldValue("city_id", newValue?.id || null);
                      // Clear search term when option is selected
                      if (newValue) {
                        setCitySearchTerm("");
                      }
                    }}
                    onInputChange={(event, newInputValue, reason) => {
                      if (reason === "input") {
                        setCitySearchTerm(newInputValue);
                      }
                    }}
                    disabled={!values.country_id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select city"
                        error={
                          (touched.city_id && Boolean(errors.city_id)) ||
                          Boolean(getServerError("city_id"))
                        }
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingCities && (
                                <CircularProgress color="inherit" size={20} />
                              )}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    ListboxProps={{
                      onScroll: (event) => {
                        const listboxNode = event.currentTarget;
                        const threshold = 100;
                        if (
                          listboxNode.scrollHeight - listboxNode.scrollTop <=
                            listboxNode.clientHeight + threshold &&
                          hasMoreCities &&
                          !loadingCities
                        ) {
                          loadMoreCities();
                        }
                      },
                    }}
                    loading={loadingCities}
                    filterOptions={(x) => x} // Disable built-in filtering
                    noOptionsText={
                      debouncedCitySearchTerm.length >= 2
                        ? "No cities found"
                        : "Type to search cities"
                    }
                  />
                  {touched.city_id && errors.city_id && (
                    <FormHelperText error>{errors.city_id}</FormHelperText>
                  )}
                  {getServerError("city_id") && (
                    <FormHelperText error>
                      {getServerError("city_id")}
                    </FormHelperText>
                  )}
                </Stack>
              </Grid>

              <Grid item xs={12}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="description">
                    <FormattedMessage id="Description" />
                  </InputLabel>
                  <TextField
                    fullWidth
                    id="description"
                    name="description"
                    value={values.description}
                    onChange={handleChange}
                    placeholder="Description"
                    multiline
                    rows={4}
                    error={
                      (touched.description && Boolean(errors.description)) ||
                      Boolean(getServerError("description"))
                    }
                  />
                  {touched.description && errors.description && (
                    <FormHelperText error>{errors.description}</FormHelperText>
                  )}
                  {getServerError("description") && (
                    <FormHelperText error>
                      {getServerError("description")}
                    </FormHelperText>
                  )}
                </Stack>
              </Grid>

              <Grid item xs={12}>
                <Stack spacing={1}>
                  <Typography>
                    <FormattedMessage id="Certificate Photo" />
                  </Typography>
                  <ImageUploader
                    id="certificate-photo"
                    previewUrl={
                      typeof values.certificate_photo === "string"
                        ? values.certificate_photo
                        : values.certificate_photo
                        ? URL.createObjectURL(values.certificate_photo)
                        : null
                    }
                    onChange={(e, setFieldValue) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setFieldValue("certificate_photo", file);
                      }
                    }}
                    setFieldValue={setFieldValue}
                    label="Click to upload certificate photo"
                  />
                  {touched.certificate_photo && errors.certificate_photo && (
                    <FormHelperText error>
                      {errors.certificate_photo}
                    </FormHelperText>
                  )}
                  {getServerError("certificate_photo") && (
                    <FormHelperText error>
                      {getServerError("certificate_photo")}
                    </FormHelperText>
                  )}
                </Stack>
              </Grid>
            </Grid>

            <DialogActions sx={{ p: 2.5 }}>
              <Grid container justifyContent="center" alignItems="center">
                <Stack direction="row" spacing={2} sx={{ width: 1 }}>
                  <Grid item md={2} />
                  <Grid item md={4}>
                    <Button
                      disabled={isSubmitting}
                      fullWidth
                      type="submit"
                      variant="contained"
                    >
                      <FormattedMessage
                        id={item && item?.id ? "Update" : "Submit"}
                      />
                    </Button>
                  </Grid>
                  <Grid item md={4}>
                    <Button
                      fullWidth
                      color="secondary"
                      variant="outlined"
                      onClick={handleClose}
                    >
                      <FormattedMessage id="Cancel" />
                    </Button>
                  </Grid>
                  <Grid item md={2} />
                </Stack>
              </Grid>
            </DialogActions>
          </Form>
        </FormikProvider>
      </DialogContent>
    </Dialog>
  );
}

export default AddEditResumeEducationForm;
